import React, { MouseEvent, useEffect, useRef, useState } from 'react';
import ReactPlayer from 'react-player';
import { OnProgressProps } from 'react-player/base';
import { useToggle } from 'react-use';
import { Common } from '@thecvlb/design-system';
import screenfull from 'screenfull';

import { useMarkPageViewedMutation } from 'services/wm/wm';

import { selectWmPages } from 'store';
import { setPages } from 'store/wm/wmSlice';

import { useAppDispatch, useAppSelector } from 'hooks';

import { WeightManagementVideoProps } from './weightManagementVideo.types';

import FastForward from 'assets/icons/fast-forward.svg?react';
import FastRewind from 'assets/icons/fast-rewind.svg?react';
import Pause from 'assets/icons/pause.svg?react';
import Play from 'assets/icons/play.svg?react';

const WeightManagementVideo: React.FC<WeightManagementVideoProps> = ({ isOpen, onClose, page }) => {
  const ref = useRef<ReactPlayer | null>(null);
  const refModal = useRef<HTMLDivElement | null>(null);
  const [showControls, setShowControls] = useToggle(true);
  const [videoState, setVideoState] = useState<
    {
      duration: number;
      loop: boolean;
      playbackRate: number;
      playing: boolean;
      volume: number;
    } & Partial<OnProgressProps>
  >({
    duration: 0,
    loaded: 0,
    loop: false,
    playbackRate: 1.0,
    played: 0,
    playing: true,
    volume: 0.8
  });

  const dispatch = useAppDispatch();
  const pages = useAppSelector(selectWmPages);
  const [markPageViewed] = useMarkPageViewedMutation();

  const handleDuration = (duration: number) => {
    setVideoState((prev) => ({ ...prev, duration }));
  };
  const handleEnded = () => {
    if (!page) return;
    markPageViewed({ wmPageId: page._id })
      .unwrap()
      .then(() => {
        dispatch(
          setPages(
            pages.map((p) =>
              p._id === page._id
                ? {
                    ...p,
                    viewed: true
                  }
                : p
            )
          )
        );
      });
  };
  const handleOnPlaybackRateChange = () => {};
  const handlePlayPause = () => {
    setVideoState((prev) => ({ ...prev, playing: !prev.playing }));
  };
  const handleClickFullscreen = () => {
    // eslint-disable-next-line @typescript-eslint/ban-ts-comment
    // @ts-ignore
    screenfull?.request(ref.current?.wrapper);
  };
  const handleSeekChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    setVideoState((prev) => ({ ...prev, played: parseFloat(e.target.value) }));
  };
  const handleSeekMouseDown = () => {
    setVideoState((prev) => ({ ...prev, seeking: true }));
  };

  const handleSeekMouseUp = (e: MouseEvent<HTMLInputElement>) => {
    ref?.current?.seekTo(parseFloat((e.target as HTMLInputElement).value));
    setVideoState((prev) => ({ ...prev, seeking: false }));
  };

  const handleFastRewind = () => {
    const time = videoState.playedSeconds || 0;
    const played = Math.max(time - videoState.duration / 20, 0) / videoState.duration;
    setVideoState((prev) => ({ ...prev, played }));
    ref?.current?.seekTo(played);
  };

  const handleFastForward = () => {
    const time = videoState.playedSeconds || 0;
    const duration = videoState.duration;
    let played = Math.min(time + duration / 20, duration) / duration;
    if (played >= 0.9999) {
      played = 0.9999;
    }
    setVideoState((prev) => ({
      ...prev,
      played,
      playing: prev.playing && played === duration ? false : prev.playing
    }));
    ref?.current?.seekTo(played);
  };

  const handleProgress = (state: OnProgressProps) => {
    setVideoState((prev) => ({ ...prev, ...state }));
  };

  useEffect(() => {
    if (!page) return;
    let timeout: ReturnType<typeof setTimeout>;

    const handleSetShowControls = () => {
      setShowControls(true);
      clearTimeout(timeout);
      timeout = setTimeout(() => setShowControls(false), 3000);
    };

    handleSetShowControls();

    refModal.current?.addEventListener('mousemove', handleSetShowControls);
    refModal.current?.addEventListener('touchstart', handleSetShowControls);
    refModal.current?.addEventListener('mouseleave', () => setShowControls(false));

    return () => {
      refModal.current?.removeEventListener('mousemove', handleSetShowControls);
      refModal.current?.removeEventListener('touchstart', handleSetShowControls);
    };
  }, [refModal, page]);

  if (!page) return null;

  return (
    <Common.Modal
      close={onClose}
      isOpen={isOpen}
      padding={false}
      size="xl"
      zIndex={40}
      hideCloseButton
    >
      <div className="relative overflow-hidden md:rounded-xl" ref={refModal}>
        <ReactPlayer
          {...videoState}
          height="100%"
          playIcon={<Play />}
          ref={ref}
          url={page?.fileUrl}
          width="100%"
          onDuration={handleDuration}
          onEnded={handleEnded}
          onPause={() => setVideoState((prev) => ({ ...prev, playing: false }))}
          onPlaybackRateChange={handleOnPlaybackRateChange}
          onProgress={handleProgress}
        />
        <div
          className={`transition-all ${showControls ? 'opacity-100' : 'pointer-events-none opacity-0'}`}
        >
          <div className="absolute inset-0 m-auto flex size-fit items-center gap-12">
            <button
              className="flex size-10 items-center justify-center rounded-full bg-black/40 fill-white"
              onClick={handleFastRewind}
            >
              <FastRewind />
            </button>
            <button
              className="flex size-14 items-center justify-center rounded-full bg-black/40 fill-white"
              onClick={handlePlayPause}
            >
              {videoState.playing ? <Pause /> : <Play />}
            </button>
            <button
              className="flex size-10 items-center justify-center rounded-full bg-black/40 fill-white"
              onClick={handleFastForward}
            >
              <FastForward />
            </button>
          </div>
          <button className="absolute right-6 top-6 text-white" onClick={handleClickFullscreen}>
            <Common.Icon className="size-7" name="fullscreen" />
          </button>
          <div className="absolute bottom-0 h-1.5 w-full">
            <input
              className="absolute z-10 size-full cursor-pointer opacity-0"
              max={1}
              min={0}
              step="any"
              type="range"
              value={videoState.played}
              onChange={handleSeekChange}
              onMouseDown={handleSeekMouseDown}
              onMouseUp={handleSeekMouseUp}
            />
            <progress className="absolute size-full" max={1} value={videoState.played} />
          </div>
        </div>
      </div>
    </Common.Modal>
  );
};

export default WeightManagementVideo;
